import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Chip,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  CircularProgress,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeader1 from "../../../components/common/PageHeader1";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { usePermissions } from "../../../context/AuthContext";
import { usePrivilegesConfig } from "../../../context/PrivilegesConfigContext";

function RolePrivilegeList() {
  const { can } = usePermissions();
  const { privilegesConfig, loading: configLoading, error: configError } = usePrivilegesConfig();
  const token = localStorage.getItem("vedhvidhi-admin-token");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalRows, setTotalRows] = useState(0);

  const getPrivilegeLabel = useCallback((privilegeString) => {
    if (!privilegeString || !privilegeString.includes(':') || !privilegesConfig || privilegesConfig.length === 0) {
      return privilegeString;
    }
    const [resource, action] = privilegeString.split(':');
    let foundLabel = privilegeString;
    const findLabelRecursive = (items) => {
      for (const item of items) {
        if (item.module === resource && item.privileges) {
          const privilege = item.privileges.find(p => p.key === action);
          if (privilege) {
            foundLabel = privilege.label;
            return true;
          }
        }
        if (item.children) {
          if (findLabelRecursive(item.children)) {
            return true;
          }
        }
      }
      return false;
    };
    findLabelRecursive(privilegesConfig);
    return foundLabel;
  }, [privilegesConfig]);

  const fetchRolesServer = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/role?page=${page}&limit=${limit}`, {
        headers: { Authorization: token },
      });
      if (response.data.success) {
        setRoles(response.data.data || []);
        setTotalRows(response.data.paginationDetail?.totalDocs || 0);
      } else {
        toast.error("Failed to fetch roles");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRolesServer(paginationModel.page + 1, paginationModel.pageSize);
  }, [paginationModel]);

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/role/${selectedId}`, {
        headers: { Authorization: token },
      });
      toast.success("Role deleted successfully");
      fetchRolesServer(paginationModel.page + 1, paginationModel.pageSize);
    } catch (err) {
      toast.error("Failed to delete role");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const canEdit = can('role_management:edit');
  const canDelete = can('role_management:delete');

  const columns = [
    { field: "sno", headerName: "S.No", width: 80 },
    { field: "roleName", headerName: "Role Name", width: 200 },
    {
      field: "privileges",
      headerName: "Privileges",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 1 }}>
          {params.row.privileges?.map((priv, idx) => (
            <Tooltip key={idx} title={getPrivilegeLabel(priv)} placement="top">
              <Chip label={getPrivilegeLabel(priv)} size="small" />
            </Tooltip>
          ))}
        </Box>
      ),
    },
    ...(canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 130,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canEdit && (
                <Tooltip title="Edit Role">
                  <IconButton component={Link} to={`/useree/${row.id}`}>
                    <i className="icofont-edit text-info"></i>
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Role">
                  <IconButton onClick={() => handleDeleteClick(row.id)}>
                    <i className="icofont-ui-delete text-danger"></i>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ]
      : []),
  ];


  const rows = roles.map((role, index) => ({
    id: role._id,
    sno: index + 1 + paginationModel.page * paginationModel.pageSize,
    roleName: role.roleName,
    privileges: role.privileges || [],
  }));

  if (configLoading) {
    return <div style={{ padding: "24px", textAlign: 'center' }}><CircularProgress /> <Typography>Loading Configurations...</Typography></div>;
  }

  if (configError) {
    return <div style={{ padding: "24px", textAlign: 'center' }}><Typography color="error">Error: {configError}</Typography></div>;
  }

  return (
    <div className="container-xxl">
      <PageHeader1
        righttitle="Add New Role"
        link="/addrole"
        routebutton={can('role_management:add')}
        pagetitle="Role Privileges"
      />
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            paginationMode="server"
            paginationModel={paginationModel}
            rowCount={totalRows}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            rowHeight={80}
          />
        </Box>
      </Paper>
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this role?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default RolePrivilegeList;